import type { GAPageType } from '@/helpers/common'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { error } from '@/services/Log'
import type { GraphQlCustomer } from '@/helpers/graphql'
import { thenCaught } from './FP'

const fetchCustomerGAModules = async () => {
  const [
    { getCustomer },
    { getCustomerOrders },
  ] = await allPromisesWithRetries(() => [
    import('@/helpers/graphql'),
    import('@/services/Orders'),
  ])
  return {
    getCustomer,
    getCustomerOrders,
  }
}

const getIdAndEmail = (maybeCustomer: GraphQlCustomer | null) => ({
  customerId: maybeCustomer?.entityId || 0,
  customerEmail: maybeCustomer?.email || '',
})

const getOrdersCount = (customerId: number) => async ({ getCustomerOrders }: { getCustomerOrders: Awaited<ReturnType<typeof fetchCustomerGAModules>>['getCustomerOrders'] }) => (
  !customerId ? 0 : (await getCustomerOrders({ sort: 'date_created:desc' })).length
)

const sendToGAHappyPath = async ({
  cartAmount,
  pageType,
}: { cartAmount: number, pageType: GAPageType }) => {
  const { getCustomer, getCustomerOrders } = await fetchCustomerGAModules()
  const { customer } = await getCustomer()
  const { customerId, customerEmail } = getIdAndEmail(customer)
  const orderCount = await getOrdersCount(customerId)({ getCustomerOrders })
  window?.dataLayer?.push({
    event: 'customer',
    visitorType: customerId ? 'Logged In' : 'Guest', // Guest OR Logged In
    customerId, // customer ID
    customerEmail, // Hashed customer email
    customerOrdersCount: orderCount, // Lifetime orders for customer
    customerTotalSpent: '', // Lifetime customer value for customer
    cartTotal: cartAmount, // should be the subtotal of the shopping cart
    pageType,
  })
}

const logErrorWhileSendingCustomerGAEvent = (err: unknown) => {
  error('Failed to send customer to GA', err)
}

const sendToGA = thenCaught(sendToGAHappyPath)(logErrorWhileSendingCustomerGAEvent)

const executeWhenLoadedAndInteractive = (fn: () => unknown) => {
  if (window.loadedAndInteractive) {
    fn()
  } else {
    window.addEventListener('loadedAndInteractive', () => {
      fn()
    }, { once: true })
  }
}

export const customerGA = (cartAmount: number, pageType: GAPageType) => (
  executeWhenLoadedAndInteractive(() => (
    sendToGA({ cartAmount, pageType })
  ))
)
